
import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import type { Contact } from '@/pages/PersonalizeOutreach';

interface CsvUploaderProps {
  onCsvUploaded: (contacts: Contact[]) => void;
}

const CsvUploader = ({ onCsvUploaded }: CsvUploaderProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState<string[][]>([]);

  const parseCSV = (text: string): string[][] => {
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map(line => {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      return values;
    });
  };

  const detectHeaders = (data: string[][]): { [key: string]: number } => {
    if (data.length === 0) return {};
    
    const headers = data[0].map(h => h.toLowerCase());
    const mapping: { [key: string]: number } = {};
    
    // Detect person/name column
    const personHeaders = ['person', 'name', 'contact', 'full name', 'contact name'];
    const personIndex = headers.findIndex(h => personHeaders.some(ph => h.includes(ph)));
    if (personIndex !== -1) mapping.person = personIndex;
    
    // Detect organization/company column
    const organizationHeaders = ['organization', 'company', 'org', 'business'];
    const organizationIndex = headers.findIndex(h => organizationHeaders.some(oh => h.includes(oh)));
    if (organizationIndex !== -1) mapping.organization = organizationIndex;
    
    // Detect website column
    const websiteHeaders = ['website', 'url', 'site', 'web', 'domain'];
    const websiteIndex = headers.findIndex(h => websiteHeaders.some(wh => h.includes(wh)));
    if (websiteIndex !== -1) mapping.website = websiteIndex;
    
    // Detect email column
    const emailHeaders = ['email', 'e-mail', 'mail'];
    const emailIndex = headers.findIndex(h => emailHeaders.some(eh => h.includes(eh)));
    if (emailIndex !== -1) mapping.email = emailIndex;
    
    // Detect title column
    const titleHeaders = ['title', 'position', 'job title', 'role'];
    const titleIndex = headers.findIndex(h => titleHeaders.some(th => h.includes(th)));
    if (titleIndex !== -1) mapping.title = titleIndex;
    
    // Detect phone column
    const phoneHeaders = ['phone', 'tel', 'telephone', 'mobile'];
    const phoneIndex = headers.findIndex(h => phoneHeaders.some(ph => h.includes(ph)));
    if (phoneIndex !== -1) mapping.phone = phoneIndex;
    
    return mapping;
  };

  const processCSV = (data: string[][]) => {
    const headerMapping = detectHeaders(data);
    const dataRows = data.slice(1); // Skip header row
    
    const contacts: Contact[] = dataRows.map((row, index) => ({
      id: `contact_${index + 1}`,
      person: row[headerMapping.person] || '',
      organization: row[headerMapping.organization] || '',
      website: row[headerMapping.website] || '',
      email: headerMapping.email !== undefined ? row[headerMapping.email] : undefined,
      title: headerMapping.title !== undefined ? row[headerMapping.title] : undefined,
      phone: headerMapping.phone !== undefined ? row[headerMapping.phone] : undefined,
    })).filter(contact => contact.person || contact.organization); // Filter out empty rows
    
    return contacts;
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setIsProcessing(true);
    
    try {
      const text = await file.text();
      const data = parseCSV(text);
      
      if (data.length < 2) {
        toast.error('CSV file must contain at least a header row and one data row');
        return;
      }
      
      setPreviewData(data.slice(0, 6)); // Show first 5 rows + header
      const contacts = processCSV(data);
      
      if (contacts.length === 0) {
        toast.error('No valid contacts found in CSV. Please ensure you have person/name and organization columns.');
        return;
      }
      
      toast.success(`Successfully loaded ${contacts.length} contacts`);
      onCsvUploaded(contacts);
      
    } catch (error) {
      console.error('Error processing CSV:', error);
      toast.error('Error processing CSV file. Please check the format.');
    } finally {
      setIsProcessing(false);
    }
  }, [onCsvUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/csv': ['.csv'],
    },
    multiple: false,
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload CSV File
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} />
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            {isDragActive ? (
              <p className="text-lg">Drop the CSV file here...</p>
            ) : (
              <div>
                <p className="text-lg mb-2">
                  Drag & drop your CSV file here, or click to select
                </p>
                <p className="text-sm text-gray-500">
                  CSV files should contain columns for person/name, organization, and website
                </p>
              </div>
            )}
            {isProcessing && (
              <div className="mt-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                <p className="text-sm text-gray-600 mt-2">Processing CSV...</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {previewData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              CSV Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    {previewData[0]?.map((header, index) => (
                      <th key={index} className="border border-gray-300 px-4 py-2 text-left font-medium">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(1).map((row, index) => (
                    <tr key={index}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="border border-gray-300 px-4 py-2">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              Showing first {Math.min(previewData.length - 1, 5)} rows of data
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CsvUploader;
